import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import api, { fileUrl } from "@/lib/apiClient";
import SEO from "@/components/SEO";
import ShareButton from "@/components/Shared";

export default function PressDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/press/${slug}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <div className="font-serif text-7xl text-npst-goldBorder">404</div>
      <h1 className="font-serif text-3xl text-npst-ink mt-4">Release not found</h1>
      <Link to="/media" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold uppercase tracking-[0.12em] text-npst-navy">← Back to Media</Link>
    </div>
  );
  if (!data) return <div className="py-32 text-center text-npst-muted animate-pulse">Loading…</div>;
  const { press: p, related_pils } = data;

  return (
    <div className="fade-in">
      <SEO route={`/media/${p.slug}`} title={`${p.seo_title || p.headline} — NPST`} description={(p.meta_description || p.excerpt || "").slice(0, 160)} canonical={`${window.location.origin}/media/${p.slug}`} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate("/media")} className="inline-flex items-center gap-2 text-sm text-npst-muted hover:text-npst-navy transition-colors"><ArrowLeft size={15} /> Media / Press Release</button>
          <ShareButton title={p.headline} />
        </div>
        {p.state !== "published" && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-md font-semibold uppercase tracking-wide mb-6">Preview · {p.state}</div>}
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-npst-gold font-semibold mb-5">{p.publish_date} · {p.category}</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-npst-ink leading-[1.1] tracking-tight" data-testid="press-headline">{p.headline}</h1>
        {p.subheadline && <p className="mt-5 font-serif text-2xl text-npst-navy leading-snug">{p.subheadline}</p>}
        {p.excerpt && <p className="mt-6 text-lg text-npst-inkSoft leading-relaxed border-l-2 border-npst-gold pl-5">{p.excerpt}</p>}
        {p.featured_image_id && <div className="my-8" />}
        <div className="prose-npst mt-10 text-base" data-testid="press-body">
          {(p.body || "").split("\n\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
        </div>
        {p.author && <p className="mt-8 text-sm text-npst-muted italic">— {p.author}</p>}
        {p.attachments?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-npst-line">
            <div className="label-eyebrow text-npst-gold mb-3">Attachments</div>
            {p.attachments.map((a) => <a key={a} href={fileUrl(a)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-npst-navy hover:text-npst-gold"><Download size={14} /> Download Original Release</a>)}
          </div>
        )}
        {related_pils?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-npst-line">
            <div className="label-eyebrow text-npst-gold mb-4">Related Litigation</div>
            {related_pils.map((pil) => (
              <Link key={pil.pil_code} to={`/pil/${pil.pil_code.toLowerCase()}`} className="block group mb-3">
                <div className="font-mono text-xs text-npst-muted">{pil.court}</div>
                <div className="font-serif text-xl text-npst-ink group-hover:text-npst-navy transition-colors">{pil.title}</div>
              </Link>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
